/**
 * ObjectiveTypesModal Component
 *
 * Modal for managing objective type categories.
 * Provides CRUD operations for the objectiveTypes array in settings.
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback } from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Alert,
  Divider
} from '@enfomentis/ui';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';

// 3. Services & Types
import { tradingObjectiveService } from '@/services/TradingObjectiveService';
import { tradingObjectiveSettingsService } from '@/services/TradingObjectiveSettingsService';
import type { ObjectiveType } from '@/types/schema/TradingObjectiveSettings';

// 4. Utils
import { generateUUID } from '@/utils/uuid';

/**
 * Props for ObjectiveTypesModal
 */
export interface ObjectiveTypesModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Optional callback when types are changed */
  onTypesChange?: (types: ObjectiveType[]) => void;
}

/**
 * ObjectiveTypesModal Component
 */
export const ObjectiveTypesModal: React.FC<ObjectiveTypesModalProps> = ({
  isOpen,
  onClose,
  onTypesChange
}) => {
  // Types state
  const [types, setTypes] = useState<ObjectiveType[]>([]);
  const [typeUsage, setTypeUsage] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add type state
  const [newTypeName, setNewTypeName] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  // Edit type state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  // Delete confirmation state
  const [deletingId, setDeletingId] = useState<string | null>(null);

  /**
   * Load types and usage data
   */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [settings, objectives] = await Promise.all([
        tradingObjectiveSettingsService.getOrCreateDefaultSettings(),
        tradingObjectiveService.getAll()
      ]);

      setTypes(settings.objectiveTypes);

      // Calculate usage counts
      const usageCounts = new Map<string, number>();
      objectives.forEach(obj => {
        const count = usageCounts.get(obj.type) || 0;
        usageCounts.set(obj.type, count + 1);
      });
      setTypeUsage(usageCounts);
    } catch (err) {
      console.error('Failed to load objective types:', err);
      setError('Failed to load objective types');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadData();
      // Reset state
      setNewTypeName('');
      setAddError(null);
      setEditingId(null);
      setDeletingId(null);
    }
  }, [isOpen, loadData]);

  /**
   * Add a new type
   */
  const handleAddType = () => {
    const trimmed = newTypeName.trim();
    setAddError(null);

    if (!trimmed) {
      setAddError('Type name is required');
      return;
    }

    // Check for duplicates (case-insensitive)
    const isDuplicate = types.some(
      t => t.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (isDuplicate) {
      setAddError('A type with this name already exists');
      return;
    }

    const newType: ObjectiveType = {
      id: generateUUID(),
      name: trimmed
    };

    setTypes(prev => [...prev, newType]);
    setNewTypeName('');
  };

  /**
   * Start editing a type
   */
  const startEditing = (type: ObjectiveType) => {
    setEditingId(type.id);
    setEditingName(type.name);
  };

  /**
   * Cancel editing
   */
  const cancelEditing = () => {
    setEditingId(null);
    setEditingName('');
  };

  /**
   * Save edited type name
   */
  const saveEdit = () => {
    if (!editingId) return;

    const trimmed = editingName.trim();
    if (!trimmed) {
      cancelEditing();
      return;
    }

    // Check for duplicates (excluding current type)
    const isDuplicate = types.some(
      t => t.id !== editingId && t.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (isDuplicate) {
      // Just cancel - don't show error in this case
      cancelEditing();
      return;
    }

    setTypes(prev =>
      prev.map(t => (t.id === editingId ? { ...t, name: trimmed } : t))
    );
    cancelEditing();
  };

  /**
   * Handle edit input key press
   */
  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  /**
   * Delete a type
   */
  const handleDeleteType = (typeId: string) => {
    const usage = typeUsage.get(typeId) || 0;

    if (usage > 0 && deletingId !== typeId) {
      // Show confirmation
      setDeletingId(typeId);
      return;
    }

    // Proceed with delete
    setTypes(prev => prev.filter(t => t.id !== typeId));
    setDeletingId(null);
  };

  /**
   * Cancel delete confirmation
   */
  const cancelDelete = () => {
    setDeletingId(null);
  };

  /**
   * Save all changes
   */
  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      await tradingObjectiveSettingsService.updateObjectiveTypes(types);
      onTypesChange?.(types);
      onClose();
    } catch (err) {
      console.error('Failed to save objective types:', err);
      setError('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Get usage count for a type
   */
  const getUsageCount = (typeId: string): number => {
    return typeUsage.get(typeId) || 0;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      placement="top-center"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader>Manage Objective Types</ModalHeader>

        <ModalBody className="gap-4">
          {/* Loading state */}
          {loading && (
            <div className="text-center py-4 text-text-secondary">
              Loading...
            </div>
          )}

          {/* Error state */}
          {error && (
            <Alert color="danger" variant="flat">
              {error}
            </Alert>
          )}

          {/* Add new type */}
          {!loading && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  placeholder="New type name"
                  value={newTypeName}
                  onValueChange={setNewTypeName}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddType()}
                  size="sm"
                  className="flex-1 [&_input]:text-base"
                />
                <Button
                  size="sm"
                  color="primary"
                  onPress={handleAddType}
                  startContent={<Plus size={16} />}
                >
                  Add
                </Button>
              </div>
              {addError && (
                <p className="text-danger text-sm">{addError}</p>
              )}
            </div>
          )}

          {/* Types list */}
          {!loading && types.length > 0 && (
            <>
              <Divider />
              <div className="space-y-2">
                {types.map((type) => {
                  const usage = getUsageCount(type.id);
                  const isEditing = editingId === type.id;
                  const isDeleting = deletingId === type.id;

                  return (
                    <div key={type.id} className="space-y-2">
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-default-100">
                        {isEditing ? (
                          // Edit mode
                          <>
                            <Input
                              value={editingName}
                              onValueChange={setEditingName}
                              onKeyDown={handleEditKeyDown}
                              size="sm"
                              className="flex-1 [&_input]:text-base"
                              autoFocus
                            />
                            <Button
                              size="sm"
                              variant="light"
                              isIconOnly
                              onPress={saveEdit}
                              color="success"
                            >
                              <Check size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="light"
                              isIconOnly
                              onPress={cancelEditing}
                            >
                              <X size={16} />
                            </Button>
                          </>
                        ) : (
                          // View mode
                          <>
                            <span className="flex-1 font-medium">{type.name}</span>
                            {usage > 0 && (
                              <span className="text-sm text-text-tertiary">
                                {usage} objective{usage !== 1 ? 's' : ''}
                              </span>
                            )}
                            <Button
                              size="sm"
                              variant="light"
                              isIconOnly
                              onPress={() => startEditing(type)}
                            >
                              <Pencil size={14} />
                            </Button>
                            <Button
                              size="sm"
                              variant="light"
                              isIconOnly
                              onPress={() => handleDeleteType(type.id)}
                              color="danger"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </>
                        )}
                      </div>

                      {/* Delete confirmation */}
                      {isDeleting && (
                        <div className="ml-2 p-2 rounded bg-danger-50 border border-danger-200">
                          <p className="text-sm text-danger mb-2">
                            This type is used by {usage} objective{usage !== 1 ? 's' : ''}.
                            Deleting it will leave those objectives without a type.
                          </p>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              color="danger"
                              onPress={() => handleDeleteType(type.id)}
                            >
                              Delete Anyway
                            </Button>
                            <Button
                              size="sm"
                              variant="flat"
                              onPress={cancelDelete}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Empty state */}
          {!loading && types.length === 0 && (
            <div className="text-center py-4 text-text-secondary">
              No types configured. Add your first type above.
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={isSaving}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSave}
            isLoading={isSaving}
            isDisabled={loading || isSaving}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ObjectiveTypesModal;
